import { PlusOutlined } from '@ant-design/icons';
import { Form, Grid, Input, Modal, Pagination, Select, Tag, Typography, message } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { accountInfoQueryKey } from 'api/account';
import type { OrganisationTree } from 'api/organisations';
import { setAnalyticsFarmContext, trackAnalyticsEvent } from 'analytics/ga4';
import { setCurrentFarmContext } from 'farmContext';
import { useSelfAddFarm } from 'hooks/useFarms';
import { billingCompaniesQueryKey } from 'hooks/useBillingCompanies';
import { organisationsQueryKey, useCreateCompany, useUserOrganisations } from 'hooks/useOrganisations';
import { storageKeys } from 'static';
import { TFunction } from 'types';
import { notifyZapierFarmRegistered } from 'utils/zapierFarmRegistered';
import { OrgHubEntityList, OrgHubEntityRow } from './OrgHubEntityRow';
import { OrgPageHero } from './OrgPageHero';
import { OrgPageLoading, OrgPageMessage, OrgReloadButton } from './OrgPageStates';
import { OrgPrimaryButton } from './OrgPrimaryButton';
import { OrgSearchField } from './OrgSearchField';
import { OrgSearchResultCard } from './OrgSearchResultCard';
import { OrgSectionHeader } from './OrgSectionHeader';
import { OrganisationsPageLayout } from './OrganisationsPageLayout';
import {
  normalizeOrganisationSearchQuery,
  searchOrganisationTree,
} from './organisationSearch';
import { orgBrandBlue, orgBrandGreen, orgCardShadow } from './organisationsUi';
import {
  canManageOrganisation,
  customerNumberLabel,
  flattenHubCompanies,
  flattenHubFarms,
  getInvitedOrganisations,
  getManagedOrganisationId,
  getOrganisationAccessKind,
  getOwnedOrganisations,
  hasAnyCompaniesOrFarms,
  isOrganisationOwned,
  type HubFarm,
} from 'utils/companyHub';

const DEFAULT_PAGE_SIZE = 20;

const panelStyle = {
  background: '#fff',
  borderRadius: 8,
  boxShadow: orgCardShadow,
  overflow: 'hidden' as const,
};

const metricCardBaseStyle = {
  padding: '14px 16px',
  borderRadius: 10,
  background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
  border: '1px solid #eaecf0',
  minWidth: 0,
};

export default function OrganisationsPage() {
  const { t }: TFunction = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const screens = Grid.useBreakpoint();
  const isCompact = !screens.md;
  const { data: organisations, isLoading, isError } = useUserOrganisations();
  const createCompany = useCreateCompany();
  const { mutateAsync: selfAddFarm, isLoading: isSavingFarm } = useSelfAddFarm();
  const [searchQuery, setSearchQuery] = useState('');
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [farmModalOpen, setFarmModalOpen] = useState(false);
  const [selectedOrganisationId, setSelectedOrganisationId] = useState<number | null>(null);
  const [companyPage, setCompanyPage] = useState(1);
  const [farmPage, setFarmPage] = useState(1);
  const [companyForm] = Form.useForm();
  const [farmForm] = Form.useForm();

  const ownedOrgs = useMemo(() => getOwnedOrganisations(organisations), [organisations]);
  const invitedOrgs = useMemo(() => getInvitedOrganisations(organisations), [organisations]);
  const allOrgs = useMemo(() => [...ownedOrgs, ...invitedOrgs], [ownedOrgs, invitedOrgs]);

  const defaultOrgId = useMemo(() => {
    if (ownedOrgs[0]?.id) {
      return ownedOrgs[0].id;
    }
    return invitedOrgs[0]?.id ?? null;
  }, [ownedOrgs, invitedOrgs]);

  useEffect(() => {
    if (!selectedOrganisationId && defaultOrgId) {
      setSelectedOrganisationId(defaultOrgId);
    }
  }, [defaultOrgId, selectedOrganisationId]);

  useEffect(() => {
    setCompanyPage(1);
    setFarmPage(1);
  }, [selectedOrganisationId]);

  const selectedOrg = useMemo(
    () => allOrgs.find((org) => org.id === selectedOrganisationId) ?? null,
    [allOrgs, selectedOrganisationId],
  );

  const managedOrgId = useMemo(
    () => getManagedOrganisationId(organisations, selectedOrganisationId),
    [organisations, selectedOrganisationId],
  );
  const canManageSelected = canManageOrganisation(selectedOrg);

  const companies = useMemo(() => {
    const list = selectedOrg ? flattenHubCompanies([selectedOrg]) : [];
    return [...list].sort((a, b) => a.name.localeCompare(b.name, 'sv'));
  }, [selectedOrg]);
  const farms = useMemo(() => {
    const list = selectedOrg ? flattenHubFarms([selectedOrg]) : [];
    return [...list].sort((a, b) =>
      String(a.farmName || '').localeCompare(String(b.farmName || ''), 'sv'),
    );
  }, [selectedOrg]);

  const pagedCompanies = useMemo(() => {
    const start = (companyPage - 1) * DEFAULT_PAGE_SIZE;
    return companies.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [companies, companyPage]);

  const pagedFarms = useMemo(() => {
    const start = (farmPage - 1) * DEFAULT_PAGE_SIZE;
    return farms.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [farms, farmPage]);

  const accessBadge = useMemo(() => {
    if (!selectedOrg) {
      return null;
    }
    const kind = getOrganisationAccessKind(selectedOrg);
    if (kind === 'owned') {
      return { label: t('CustomerNumberOwnBadge'), color: 'success' as const };
    }
    if (kind === 'orgAdmin') {
      return { label: t('CustomerNumberAdminBadge'), color: 'processing' as const };
    }
    if (kind === 'hencolAdmin') {
      return { label: t('CustomerNumberHencolAdminBadge'), color: 'purple' as const };
    }
    return { label: t('CustomerNumberInvitedBadge'), color: 'default' as const };
  }, [selectedOrg, t]);

  const customerSelectOptions = useMemo(() => {
    const options: { label: string; options: { value: number; label: string }[] }[] = [];
    if (ownedOrgs.length) {
      options.push({
        label: t('CustomerNumberOwnSection'),
        options: ownedOrgs.map((org) => ({
          value: org.id,
          label: formatOrgOption(org, t),
        })),
      });
    }
    if (invitedOrgs.length) {
      options.push({
        label: t('CustomerNumberInvitedSection'),
        options: invitedOrgs.map((org) => ({
          value: org.id,
          label: formatOrgOption(org, t),
        })),
      });
    }
    return options;
  }, [invitedOrgs, ownedOrgs, t]);

  const managedOrgOptions = useMemo(
    () =>
      (organisations ?? [])
        .filter((org) => canManageOrganisation(org))
        .map((org) => ({
          value: org.id,
          label: formatOrgOption(org, t),
        })),
    [organisations, t],
  );

  const normalizedQuery = normalizeOrganisationSearchQuery(searchQuery);
  const hasSearchQuery = normalizedQuery.length > 0;
  const { hits } = useMemo(
    () => searchOrganisationTree(organisations ?? [], searchQuery),
    [organisations, searchQuery],
  );
  const visibleHits = useMemo(
    () => hits.filter((hit) => hit.kind !== 'organisation'),
    [hits],
  );

  useEffect(() => {
    if ((location.state as { openAddFarm?: boolean } | null)?.openAddFarm) {
      if (canManageSelected && selectedOrganisationId) {
        farmForm.setFieldsValue({ organisationId: selectedOrganisationId });
        setFarmModalOpen(true);
      } else if (managedOrgId) {
        farmForm.setFieldsValue({ organisationId: managedOrgId });
        setFarmModalOpen(true);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [canManageSelected, farmForm, location.pathname, location.state, managedOrgId, navigate, selectedOrganisationId]);

  const kindLabel = (kind: 'organisation' | 'company' | 'farm') => {
    if (kind === 'company') {
      return t('OrgFlowSearchResultCompany');
    }
    return t('OrgFlowSearchResultFarm');
  };

  const openSearchHit = (hit: (typeof hits)[number]) => {
    if (hit.kind === 'company') {
      if (hit.orgId) {
        setSelectedOrganisationId(hit.orgId);
      }
      setSearchQuery('');
      return;
    }

    if (hit.kind === 'farm' && hit.farmId) {
      if (hit.orgId) {
        setSelectedOrganisationId(hit.orgId);
      }
      setCurrentFarmContext({
        farmId: String(hit.farmId),
        farmName: hit.label.trim() || t('CompaniesAndFarmsUnnamedFarm'),
      });
      navigate(`/farm-details?id=${hit.farmId}`);
    }
  };

  const submitCompany = async () => {
    const values = await companyForm.validateFields();
    const organisationId = values.organisationId || managedOrgId;
    if (!organisationId) {
      message.error(t('OrganisationsPageCreateError'));
      return;
    }
    try {
      await createCompany.mutateAsync({
        organisationId,
        name: values.name,
        companyRegistration: values.companyRegistration || '',
        companyType: values.companyType || 'O',
      });
      setSelectedOrganisationId(Number(organisationId));
      setCompanyModalOpen(false);
      companyForm.resetFields();
      message.success(t('OrganisationsPageCreateCompanySuccess'));
    } catch {
      message.error(t('OrganisationsPageCreateError'));
    }
  };

  const submitFarm = async () => {
    const values = await farmForm.validateFields();
    const organisationId = values.organisationId || managedOrgId;
    if (!organisationId) {
      message.error(t('OrganisationsPageCreateError'));
      return;
    }

    try {
      const farmName = String(values.farmName || '').trim();
      const data = await selfAddFarm({
        farmName,
        organisationId,
        countryId: 45,
        prodPlaceData: [],
        addressData: {
          address1: String(values.address1 || '').trim(),
          city: String(values.city || '').trim(),
          district: '',
          zipCode: '',
          address2: '',
        },
        contactData: {
          firstName: String(values.firstName || '').trim(),
          lastName: String(values.lastName || '').trim(),
          companyName: '',
          companyRegistration: '',
          companyType: 'O',
          email: '',
          fax: '',
          mobilePhone: '',
          phone: '',
        },
        cdbSynchEnabled: false,
        farmCode: '',
      });
      const farmId = data?.id ?? data?.farmId;
      if (farmId) {
        sessionStorage.setItem(storageKeys.RecentFarmId, String(farmId));
        if (farmName) {
          sessionStorage.setItem(storageKeys.RecentFarmName, farmName);
        }
        setCurrentFarmContext({
          farmId: String(farmId),
          farmName,
        });
      }
      setAnalyticsFarmContext(farmId ? String(farmId) : undefined, farmName);
      trackAnalyticsEvent('org_flow_add_farm_success');
      void notifyZapierFarmRegistered({
        farmId,
        farmName,
        firstName: String(values.firstName || '').trim(),
        lastName: String(values.lastName || '').trim(),
        address: String(values.address1 || '').trim(),
        city: String(values.city || '').trim(),
      });
      void queryClient.invalidateQueries(accountInfoQueryKey());
      void queryClient.invalidateQueries(organisationsQueryKey());
      void queryClient.invalidateQueries(billingCompaniesQueryKey());
      setSelectedOrganisationId(Number(organisationId));
      setFarmModalOpen(false);
      farmForm.resetFields();
      message.success(t('OrgFlowFarmCreated'));
      navigate('/app-selection', { replace: true, state: { newFarmId: farmId } });
    } catch {
      message.error(t('AnErrorOccurred'));
    }
  };

  if (isLoading) {
    return <OrgPageLoading />;
  }

  if (isError) {
    return (
      <OrgPageMessage
        message={t('OrganisationsPageLoadError')}
        action={<OrgReloadButton label={t('Back')} onClick={() => window.location.reload()} />}
      />
    );
  }

  if (!hasAnyCompaniesOrFarms(organisations) && ownedOrgs.length === 0 && invitedOrgs.length === 0) {
    return (
      <OrganisationsPageLayout>
        <div style={{ ...panelStyle, textAlign: 'center', padding: isCompact ? 20 : 32 }}>
          <Typography.Title level={2} style={{ marginTop: 0 }}>{t('OrgFlowWelcomeTitle')}</Typography.Title>
          <Typography.Paragraph type="secondary" style={{ maxWidth: 480, margin: '0 auto 24px' }}>
            {t('OrgFlowWelcomeText')}
          </Typography.Paragraph>
          <OrgPrimaryButton block={isCompact} onClick={() => navigate('/companies/setup')}>
            {t('OrgFlowStartSetup')}
          </OrgPrimaryButton>
        </div>
      </OrganisationsPageLayout>
    );
  }

  const createTargetOrgId = canManageSelected && selectedOrganisationId
    ? selectedOrganisationId
    : managedOrgId;

  const openFarm = (farm: HubFarm) => {
    const farmName = farm.farmName?.trim() || t('CompaniesAndFarmsUnnamedFarm');
    setCurrentFarmContext({
      farmId: String(farm.id),
      farmName,
    });
    navigate(`/farm-details?id=${farm.id}`);
  };

  const openAddFarm = () => {
    if (!createTargetOrgId) {
      return;
    }
    farmForm.setFieldsValue({ organisationId: createTargetOrgId });
    setFarmModalOpen(true);
  };

  const openAddCompany = () => {
    if (!createTargetOrgId) {
      return;
    }
    companyForm.setFieldsValue({ organisationId: createTargetOrgId });
    setCompanyModalOpen(true);
  };

  return (
    <OrganisationsPageLayout>
      <OrgPageHero
        title={t('CompaniesAndFarmsPageTitle')}
        subtitle={t('CompaniesAndFarmsHubSubtitle')}
        action={(
          <div style={{ minWidth: isCompact ? '100%' : 320 }}>
            <Typography.Text style={{ display: 'block', marginBottom: 6, color: '#667085', fontWeight: 600 }}>
              {t('CustomerNumberLabel')}
            </Typography.Text>
            <Select
              size="large"
              style={{ width: '100%' }}
              value={selectedOrganisationId ?? undefined}
              options={customerSelectOptions}
              onChange={(value) => setSelectedOrganisationId(Number(value))}
              placeholder={t('CustomerNumberSelectPlaceholder')}
            />
          </div>
        )}
      />

      {selectedOrg ? (
        <div
          style={{
            ...panelStyle,
            padding: isCompact ? '16px' : '18px 20px',
            marginBottom: 16,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 55%, #eff6ff 100%)',
            border: '1px solid #eaecf0',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: isCompact ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isCompact ? 'stretch' : 'center',
              gap: 12,
              marginBottom: 14,
            }}
          >
            <div style={{ minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
              {accessBadge ? (
                <Tag color={accessBadge.color} style={{ margin: 0, borderRadius: 999, paddingInline: 10 }}>
                  {accessBadge.label}
                </Tag>
              ) : null}
              <Typography.Text style={{ color: '#667085', fontSize: 14, lineHeight: 1.45 }}>
                {canManageSelected ? t('CustomerNumberOwnedHint') : t('CustomerNumberInvitedHint')}
              </Typography.Text>
            </div>

            {canManageSelected ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <OrgPrimaryButton icon={<PlusOutlined />} block={isCompact} onClick={openAddFarm}>
                  {t('OrgFlowAddFarm')}
                </OrgPrimaryButton>
                <OrgPrimaryButton icon={<PlusOutlined />} block={isCompact} onClick={openAddCompany}>
                  {t('OrganisationsPageAddCompany')}
                </OrgPrimaryButton>
              </div>
            ) : null}
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isCompact ? '1fr' : 'repeat(2, minmax(160px, 240px))',
              gap: 12,
            }}
          >
            <div style={{ ...metricCardBaseStyle, borderLeft: `3px solid ${orgBrandBlue}` }}>
              <Typography.Text style={{ display: 'block', fontSize: 28, fontWeight: 700, lineHeight: 1, color: '#101828' }}>
                {companies.length}
              </Typography.Text>
              <Typography.Text style={{ display: 'block', marginTop: 8, fontSize: 13, fontWeight: 600, color: '#667085' }}>
                {t('OrgFlowCompaniesStat')}
              </Typography.Text>
            </div>
            <div style={{ ...metricCardBaseStyle, borderLeft: `3px solid ${orgBrandGreen}` }}>
              <Typography.Text style={{ display: 'block', fontSize: 28, fontWeight: 700, lineHeight: 1, color: '#101828' }}>
                {farms.length}
              </Typography.Text>
              <Typography.Text style={{ display: 'block', marginTop: 8, fontSize: 13, fontWeight: 600, color: '#667085' }}>
                {t('OrgFlowFarms')}
              </Typography.Text>
            </div>
          </div>
        </div>
      ) : null}

      <OrgSearchField
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder={t('CompaniesAndFarmsSearchPlaceholder')}
      />

      {hasSearchQuery ? (
        <>
          <OrgSectionHeader
            title={t('OrgFlowSearchResults')}
            description={
              visibleHits.length
                ? t('OrgFlowSearchResultsCount', { count: visibleHits.length })
                : t('OrgFlowSearchNoResults')
            }
          />
          {visibleHits.length ? (
            <div style={{ display: 'grid', gap: 8 }}>
              {visibleHits.map((hit) => (
                <OrgSearchResultCard
                  key={hit.key}
                  hit={{
                    ...hit,
                    label:
                      hit.label.trim() ||
                      (hit.kind === 'farm'
                        ? t('CompaniesAndFarmsUnnamedFarm')
                        : t('CompaniesAndFarmsUnnamedCompany')),
                  }}
                  kindLabel={kindLabel(hit.kind)}
                  onClick={() => openSearchHit(hit)}
                />
              ))}
            </div>
          ) : (
            <section style={{ ...panelStyle, padding: '24px 20px', textAlign: 'center' }}>
              <Typography.Text style={{ color: '#667085' }}>{t('OrgFlowSearchNoResults')}</Typography.Text>
            </section>
          )}
        </>
      ) : (
        <>
          <OrgSectionHeader
            title={t('CompaniesAndFarmsCompaniesSection')}
            description={t('CompaniesAndFarmsCompaniesHint')}
          />
          <OrgHubEntityList
            empty={companies.length === 0}
            emptyLabel={t('OrganisationsPageNoCompanies')}
            footer={
              companies.length > DEFAULT_PAGE_SIZE ? (
                <Pagination
                  size="small"
                  current={companyPage}
                  pageSize={DEFAULT_PAGE_SIZE}
                  total={companies.length}
                  onChange={setCompanyPage}
                  showSizeChanger={false}
                />
              ) : null
            }
          >
            {pagedCompanies.map((company) => (
              <OrgHubEntityRow
                key={`${company.orgId}-${company.id}`}
                kind="company"
                kindLabel={t('OrgFlowSearchResultCompany')}
                title={company.name?.trim() || t('CompaniesAndFarmsUnnamedCompany')}
                detail={
                  company.companyRegistration
                    ? `${t('CompaniesAndFarmsTableRegistration')}: ${company.companyRegistration}`
                    : undefined
                }
              />
            ))}
          </OrgHubEntityList>

          <OrgSectionHeader
            title={t('CompaniesAndFarmsFarmsSection')}
            description={t('CompaniesAndFarmsFarmsHint')}
          />
          <OrgHubEntityList
            empty={farms.length === 0}
            emptyLabel={t('OrganisationsPageNoFarms')}
            footer={
              farms.length > DEFAULT_PAGE_SIZE ? (
                <Pagination
                  size="small"
                  current={farmPage}
                  pageSize={DEFAULT_PAGE_SIZE}
                  total={farms.length}
                  onChange={setFarmPage}
                  showSizeChanger={false}
                />
              ) : null
            }
          >
            {pagedFarms.map((farm) => (
              <OrgHubEntityRow
                key={`${farm.orgId}-${farm.id}`}
                kind="farm"
                kindLabel={t('OrgFlowSearchResultFarm')}
                title={farm.farmName?.trim() || t('CompaniesAndFarmsUnnamedFarm')}
                detail={
                  farm.farmCode
                    ? `${t('CompaniesAndFarmsTableCode')}: ${farm.farmCode}`
                    : t('CompaniesAndFarmsOpenFarmHint')
                }
                onClick={() => openFarm(farm)}
              />
            ))}
          </OrgHubEntityList>
        </>
      )}

      <Modal
        title={t('OrganisationsPageAddCompany')}
        open={companyModalOpen}
        onCancel={() => setCompanyModalOpen(false)}
        onOk={submitCompany}
        confirmLoading={createCompany.isLoading}
        okButtonProps={{ style: { borderRadius: 8 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
        centered
        destroyOnClose
      >
        <Form
          form={companyForm}
          layout="vertical"
          initialValues={{ companyType: 'O', organisationId: managedOrgId }}
        >
          {managedOrgOptions.length > 1 && (
            <Form.Item name="organisationId" label={t('CustomerNumberLabel')} rules={[{ required: true }]}>
              <Select size="large" options={managedOrgOptions} />
            </Form.Item>
          )}
          {managedOrgOptions.length === 1 && (
            <Form.Item name="organisationId" hidden>
              <Input />
            </Form.Item>
          )}
          <Form.Item name="name" label={t('OrganisationsPageCompanyNameLabel')} rules={[{ required: true }]}>
            <Input size="large" />
          </Form.Item>
          <Form.Item name="companyRegistration" label={t('CompanyRegistration')}>
            <Input size="large" placeholder="556000-0000" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('OrgFlowAddFarm')}
        open={farmModalOpen}
        onCancel={() => {
          if (isSavingFarm) {
            return;
          }
          setFarmModalOpen(false);
        }}
        onOk={() => {
          void submitFarm();
        }}
        confirmLoading={isSavingFarm}
        okText={t('OrgFlowCreateFarm')}
        cancelText={t('Cancel')}
        okButtonProps={{ style: { borderRadius: 8 } }}
        cancelButtonProps={{ style: { borderRadius: 8 }, disabled: isSavingFarm }}
        centered
        destroyOnClose
        width={isCompact ? undefined : 520}
      >
        <Form form={farmForm} layout="vertical" requiredMark initialValues={{ organisationId: managedOrgId }}>
          {managedOrgOptions.length > 1 && (
            <Form.Item name="organisationId" label={t('CustomerNumberLabel')} rules={[{ required: true }]}>
              <Select size="large" options={managedOrgOptions} />
            </Form.Item>
          )}
          {managedOrgOptions.length === 1 && (
            <Form.Item name="organisationId" hidden>
              <Input />
            </Form.Item>
          )}
          <Form.Item
            name="farmName"
            label={t('FarmName')}
            rules={[{ required: true, message: t('WhatIsYourFarmName') }]}
          >
            <Input size="large" placeholder={t('FarmName')} />
          </Form.Item>
          <Form.Item name="firstName" label={t('FirstName')} rules={[{ required: true }]}>
            <Input size="large" placeholder={t('FirstName')} />
          </Form.Item>
          <Form.Item name="lastName" label={t('LastName')} rules={[{ required: true }]}>
            <Input size="large" placeholder={t('LastName')} />
          </Form.Item>
          <Form.Item name="address1" label={t('Address')} rules={[{ required: true }]}>
            <Input size="large" placeholder={t('Address')} />
          </Form.Item>
          <Form.Item name="city" label={t('City')} rules={[{ required: true }]}>
            <Input size="large" placeholder={t('City')} />
          </Form.Item>
        </Form>
      </Modal>
    </OrganisationsPageLayout>
  );
}

function formatOrgOption(org: OrganisationTree, t: TFunction['t']) {
  const code = customerNumberLabel(org);
  const kind = getOrganisationAccessKind(org);
  let ownership = t('CustomerNumberInvitedBadge');
  if (kind === 'owned') {
    ownership = t('CustomerNumberOwnBadge');
  } else if (kind === 'orgAdmin') {
    ownership = t('CustomerNumberAdminBadge');
  } else if (kind === 'hencolAdmin') {
    ownership = t('CustomerNumberHencolAdminBadge');
  }
  return `${code} (${ownership})`;
}
